
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Maximize2, Camera, Filter } from 'lucide-react';
import { companyInfo, services } from '../data';

const images = [
  {
    url: "/kundebilleder/imageye___-_imgi_67_666971542_122134739847028357_2233004686136187369_n.jpeg",
    title: "Haveprojekt",
    category: "Nyanlæg"
  },
  {
    url: "/kundebilleder/imageye___-_imgi_67_667405123_122135105097028357_7106171026018513753_n.jpeg",
    title: "Flisearbejde",
    category: "Grøn vedligehold"
  },
  {
    url: "/kundebilleder/imageye___-_imgi_67_580119984_122112884895028357_8356522089783807349_n.jpeg",
    title: "Havevedligeholdelse",
    category: "Belægning & Omlægning"
  },
  {
    url: "/kundebilleder/imageye___-_imgi_67_547669702_122096651931028357_6994262535312933834_n.jpeg",
    title: "Ny terrasse",
    category: "Belægning & Omlægning"
  },
  {
    url: "/kundebilleder/imageye___-_imgi_67_662824623_122134739907028357_8056706768858079315_n.jpeg",
    title: "Gravearbejde",
    category: "Nyanlæg"
  },
  {
    url: "/kundebilleder/imageye___-_imgi_67_561726936_122107579503028357_2110243252969052305_n.jpeg",
    title: "Hækklipning",
    category: "Hækkeklip"
  },
  {
    url: "/kundebilleder/imageye___-_imgi_67_548783026_122097008931028357_2922094312626478033_n.jpeg",
    title: "Brolægning",
    category: "Belægning & Omlægning"
  },
  {
    url: "/kundebilleder/imageye___-_imgi_67_673808045_122135999193028357_9162352892792381928_n.jpeg",
    title: "Flisegang",
    category: "Belægning & Omlægning"
  },
  {
    url: "/kundebilleder/imageye___-_imgi_67_672196004_122135999001028357_8116323758983306129_n.jpeg",
    title: "Havepleje",
    category: "Belægning & Omlægning"
  },
  {
    url: "/kundebilleder/imageye___-_imgi_67_549612918_122097008937028357_6381845376905140419_n.jpeg",
    title: "Beskæring",
    category: "Beskæring & Træfældning"
  },
  {
    url: "/kundebilleder/imageye___-_imgi_67_667357066_122134739817028357_4663739399389733083_n.jpeg",
    title: "Rullegræs etablering",
    category: "Græsplæner & Rullegræs"
  },
  {
    url: "/kundebilleder/imageye___-_imgi_67_668583084_122135105049028357_2729473811509692273_n.jpeg",
    title: "Ny græsplæne",
    category: "Grøn vedligehold"
  },
  {
    url: "/kundebilleder/imageye___-_imgi_67_668896790_122135104761028357_6348603269610059595_n.jpeg",
    title: "Opbygning af mur",
    category: "Støttemur"
  },
  {
    url: "/kundebilleder/imageye___-_imgi_67_579930003_122112884889028357_6553736803778612460_n.jpeg",
    title: "Støttemur detalje",
    category: "Støttemur"
  }
];

const categories = ["Alle", ...services.map(s => s.title)];

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeCategory, setActiveCategory] = useState("Alle");

  const filteredImages = activeCategory === "Alle" 
    ? images 
    : images.filter(img => img.category === activeCategory);

  return (
    <div className="pt-24 min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#1b4c2e] text-white py-16 md:py-24 relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h3 className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-green-400 mb-4">
              Vores arbejde
            </h3>
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
              Galleri & <span className="text-green-400">Inspiration</span>
            </h1>
            <p className="text-lg md:text-xl text-green-50/80 leading-relaxed max-w-2xl">
              Gå på opdagelse i vores tidligere projekter. Vi sætter en ære i at levere høj kvalitet og smukke resultater til vores kunder.
            </p>
          </div>
        </div>
        {/* Background Decor */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-[#245e39] skew-x-12 translate-x-1/2 opacity-20" />
      </section>

      {/* Filter & Gallery Grid */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          
          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${
                  activeCategory === category
                    ? "bg-green-600 text-white shadow-lg shadow-green-600/20"
                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredImages.map((image, index) => (
              <div
                key={image.url}
                className="group relative aspect-square overflow-hidden rounded-2xl bg-gray-200 cursor-pointer shadow-sm hover:shadow-xl transition-all duration-500"
                onClick={() => setSelectedImage(image)}
              >
                {image.url && image.url !== "" && (
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="bg-white/20 backdrop-blur-md p-3 rounded-full text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <Maximize2 size={24} />
                  </div>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="bg-green-600/90 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                    {image.category}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white font-bold flex items-center gap-2">
                    <Camera size={16} className="text-green-400" />
                    {image.title}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {filteredImages.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">Ingen billeder fundet i denne kategori.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-6 right-6 text-white hover:text-green-400 transition-colors z-50 p-2 bg-white/10 rounded-full"
              onClick={() => setSelectedImage(null)}
            >
              <X size={32} />
            </button>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-5xl w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
              />
              <div className="absolute bottom-0 left-0 right-0 py-6 text-center text-white">
                <h2 className="text-2xl font-bold">{selectedImage.title}</h2>
                <p className="text-green-400 font-medium uppercase tracking-widest text-sm mt-2">{selectedImage.category}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
              Skal vi hjælpe dig med dit næste projekt?
            </h2>
            <p className="text-gray-600 mb-10 text-lg">
              Kontakt os i dag for en uforpligtende snak om dine drømme for haven eller hjemmet.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/kontakt"
                className="bg-green-600 text-white px-8 py-3.5 rounded-xl font-bold hover:bg-green-700 transition-all shadow-lg shadow-[#1b4c2e]/10 w-[289px] sm:w-fit text-center"
              >
                Få et uforpligtende tilbud
              </Link>
              <a
                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                className="bg-gray-100 text-gray-900 px-8 py-3.5 rounded-xl font-bold hover:bg-gray-200 transition-all w-[289px] sm:w-fit text-center"
              >
                Ring til os nu
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
